import { useState, useEffect } from "react";
import { Link, useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, ChevronLeft, ChevronRight, RotateCw, Crown, Check, X, HelpCircle } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { VocabularyWord, ProficiencyLevel } from "@/types/vocabulary";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { FREE_TIER_LIMITS } from "@/types/user";
import { PageContainer } from "@/components/layout/PageContainer";

export default function Flashcards() {
  const { userProfile, isAuthenticated } = useAuth();
  const { toast } = useToast();
  const [, setLocation] = useLocation();
  const [words, setWords] = useState<VocabularyWord[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  const [loading, setLoading] = useState(true);
  const [cardsStudiedToday, setCardsStudiedToday] = useState(0);

  useEffect(() => {
    if (isAuthenticated && userProfile) {
      loadVocabulary();
    }
  }, [isAuthenticated, userProfile]);

  const loadVocabulary = async () => {
    if (!userProfile) return;

    try {
      setLoading(true);
      const response = await apiRequest("GET", `/api/vocabulary/${userProfile.uid}`);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json() as VocabularyWord[];

      // Filter only "unknown" and "unsure" words for review
      const reviewWords = data.filter((w: VocabularyWord) =>
        w.proficiency === "unknown" || w.proficiency === "unsure"
      );

      setWords(reviewWords.length > 0 ? reviewWords : data);
    } catch (error) {
      console.error("Error loading vocabulary:", error);
      toast({
        title: "로딩 실패",
        description: "단어장을 불러오는 중 오류가 발생했습니다.",
        variant: "destructive",
      });
      setWords([]);
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    if (currentIndex < words.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setIsFlipped(false);
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      setIsFlipped(false);
    }
  };

  const handleFlip = () => {
    setIsFlipped(!isFlipped);
    // 무료 회원은 플립할 때마다 카운트
    if (userProfile?.role === "free") {
      setCardsStudiedToday(prev => prev + 1);
    }
  };

  const updateProficiency = async (wordId: string, proficiency: ProficiencyLevel) => {
    try {
      await apiRequest("PATCH", `/api/vocabulary/${wordId}/proficiency`, { proficiency });

      // Update local state
      setWords(words.map(w => w.id === wordId ? { ...w, proficiency } : w));

      toast({
        title: "숙련도 업데이트됨",
        description: `단어를 "${proficiency === "known" ? "알고있음" : proficiency === "unsure" ? "아리송" : "모름"}"으로 표시했습니다.`,
      });

      // Move to next card automatically if marked as "known"
      if (proficiency === "known" && currentIndex < words.length - 1) {
        setTimeout(() => {
          setCurrentIndex(currentIndex + 1);
          setIsFlipped(false);
        }, 500);
      }
    } catch (error) {
      console.error("Error updating proficiency:", error);
      toast({
        title: "업데이트 실패",
        description: "숙련도 업데이트 중 오류가 발생했습니다.",
        variant: "destructive",
      });
    }
  };

  const handleGoBack = () => {
    // 브라우저 히스토리를 사용하여 이전 페이지로
    if (window.history.length > 1) {
      window.history.back();
    } else {
      setLocation("/vocabulary");
    }
  };

  const isPremium = userProfile?.role === "premium";
  const hasReachedLimit = !isPremium && cardsStudiedToday >= FREE_TIER_LIMITS.flashcardsPerDay;

  const currentWord = words[currentIndex];

  // Login gate
  if (!isAuthenticated || !userProfile) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <RotateCw className="h-16 w-16 text-muted-foreground mb-4" />
            <h2 className="text-xl font-semibold mb-2">로그인이 필요합니다</h2>
            <p className="text-muted-foreground text-center mb-6">
              플래시카드를 사용하려면 먼저 로그인해주세요.
            </p>
            <Link href="/">
              <Button>홈으로 돌아가기</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">플래시카드 준비 중...</p>
        </div>
      </div>
    );
  }

  if (words.length === 0) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <Button variant="ghost" className="mb-6" onClick={handleGoBack} data-testid="button-back">
            <ArrowLeft className="h-4 w-4 mr-2" />
            이전으로
          </Button>

          <Card>
            <CardContent className="flex flex-col items-center justify-center py-16">
              <RotateCw className="h-16 w-16 text-muted-foreground mb-4" />
              <h2 className="text-xl font-semibold mb-2">복습할 단어가 없습니다</h2>
              <p className="text-muted-foreground text-center mb-6">
                단어장에 단어를 추가하거나, "모름" 또는 "아리송"으로 표시된 단어가 있어야 플래시카드를 사용할 수 있습니다.
              </p>
              <Link href="/vocabulary">
                <Button>단어장 보기</Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <Button variant="ghost" className="mb-6" onClick={handleGoBack} data-testid="button-back">
          <ArrowLeft className="h-4 w-4 mr-2" />
          이전으로
        </Button>

        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold mb-2">플래시카드</h1>
              <p className="text-muted-foreground">
                카드를 뒤집어 단어의 뜻을 확인하세요
              </p>
            </div>
            {!isPremium && (
              <Badge variant="outline">
                {cardsStudiedToday} / {FREE_TIER_LIMITS.flashcardsPerDay} 학습
              </Badge>
            )}
          </div>
        </div>

        {/* Free tier limit warning */}
        {hasReachedLimit && (
          <Card className="mb-6 border-primary/50 bg-primary/5">
            <CardContent className="py-4">
              <div className="flex items-start gap-3">
                <Crown className="h-5 w-5 text-primary mt-0.5" />
                <div className="flex-1">
                  <div className="font-medium">오늘의 학습 한도에 도달했습니다!</div>
                  <div className="text-sm text-muted-foreground mt-1">
                    무료 회원은 하루 {FREE_TIER_LIMITS.flashcardsPerDay}장까지 플래시카드를 학습할 수 있습니다.
                    프리미엄으로 업그레이드하여 무제한으로 학습하세요.
                  </div>
                  <Button
                    size="sm"
                    className="mt-2"
                    onClick={() => setLocation("/pricing")}
                    data-testid="button-upgrade-flashcards"
                  >
                    프리미엄 업그레이드
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Progress */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-muted-foreground">
              진행 상황: {currentIndex + 1} / {words.length}
            </span>
            <Badge variant="outline">
              {Math.round(((currentIndex + 1) / words.length) * 100)}% 완료
            </Badge>
          </div>
          <div className="w-full bg-muted rounded-full h-2">
            <div
              className="bg-primary h-2 rounded-full transition-all duration-300"
              style={{ width: `${((currentIndex + 1) / words.length) * 100}%` }}
            />
          </div>
        </div>

        {/* Flashcard */}
        <div className="mb-8" style={{ perspective: '1000px' }}>
          <div
            className="relative cursor-pointer"
            onClick={handleFlip}
            style={{
              transformStyle: 'preserve-3d',
              transition: 'transform 0.6s',
              transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
            }}
          >
            {/* Front side */}
            <Card
              className="h-[400px]"
              style={{
                backfaceVisibility: 'hidden',
                WebkitBackfaceVisibility: 'hidden',
                position: isFlipped ? 'absolute' : 'relative',
                width: '100%',
              }}
              data-testid="flashcard-front"
            >
              <CardContent className="flex flex-col items-center justify-center h-full p-8">
                <div className="text-center w-full">
                  <p className="text-5xl font-bold mb-6">{currentWord.word}</p>
                  {currentWord.pronunciation && (
                    <p className="text-lg text-muted-foreground mb-4">
                      {currentWord.pronunciation}
                    </p>
                  )}
                  <Badge variant="outline" className="text-sm">
                    {currentWord.proficiency === "unknown" ? "모름" :
                     currentWord.proficiency === "unsure" ? "아리송" : "알고있음"}
                  </Badge>
                  <div className="mt-8 p-4 bg-muted/30 rounded-md">
                    <p className="text-sm text-muted-foreground">
                      💡 카드를 클릭하여 뜻 보기
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Back side */}
            <Card
              className="h-[400px]"
              style={{
                backfaceVisibility: 'hidden',
                WebkitBackfaceVisibility: 'hidden',
                transform: 'rotateY(180deg)',
                position: isFlipped ? 'relative' : 'absolute',
                top: 0,
                width: '100%',
              }}
              data-testid="flashcard-back"
            >
              <CardContent className="flex flex-col items-center justify-center h-full p-8">
                <div className="text-center space-y-4 w-full">
                  <p className="text-3xl font-bold text-primary mb-4">{currentWord.word}</p>

                  <div>
                    <h3 className="text-xs font-semibold text-muted-foreground mb-2 uppercase">정의</h3>
                    <p className="text-base">{currentWord.definition}</p>
                  </div>

                  {currentWord.koreanTranslation && (
                    <div className="pt-2">
                      <h3 className="text-xs font-semibold text-muted-foreground mb-2 uppercase">한글 뜻</h3>
                      <p className="text-2xl font-semibold text-primary">
                        {currentWord.koreanTranslation}
                      </p>
                    </div>
                  )}

                  {currentWord.exampleSentence && (
                    <div className="pt-2">
                      <h3 className="text-xs font-semibold text-muted-foreground mb-1 uppercase">예문</h3>
                      <p className="text-xs italic text-muted-foreground line-clamp-2">
                        {currentWord.exampleSentence}
                      </p>
                    </div>
                  )}

                  <div className="mt-4 p-2 bg-muted/20 rounded-md">
                    <p className="text-xs text-muted-foreground">
                      💡 카드를 클릭하여 단어 보기
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Proficiency Update Buttons */}
        {isFlipped && (
          <div className="mb-6">
            <h3 className="text-sm font-semibold mb-3 text-center">이 단어를 얼마나 알고 계신가요?</h3>
            <div className="flex gap-2 justify-center">
              <Button
                variant={currentWord.proficiency === "unknown" ? "default" : "outline"}
                size="sm"
                onClick={() => updateProficiency(currentWord.id, "unknown")}
                className="flex-1 max-w-[150px]"
                data-testid="button-mark-unknown"
              >
                <X className="h-4 w-4 mr-1" />
                모름
              </Button>
              <Button
                variant={currentWord.proficiency === "unsure" ? "default" : "outline"}
                size="sm"
                onClick={() => updateProficiency(currentWord.id, "unsure")}
                className="flex-1 max-w-[150px]"
                data-testid="button-mark-unsure"
              >
                <HelpCircle className="h-4 w-4 mr-1" />
                아리송
              </Button>
              <Button
                variant={currentWord.proficiency === "known" ? "default" : "outline"}
                size="sm"
                onClick={() => updateProficiency(currentWord.id, "known")}
                className="flex-1 max-w-[150px]"
                data-testid="button-mark-known"
              >
                <Check className="h-4 w-4 mr-1" />
                알고있음
              </Button>
            </div>
          </div>
        )}

        {/* Navigation */}
        <div className="flex items-center justify-between gap-4">
          <Button
            variant="outline"
            size="lg"
            onClick={handlePrevious}
            disabled={currentIndex === 0}
            data-testid="button-previous"
          >
            <ChevronLeft className="h-5 w-5 mr-2" />
            이전
          </Button>

          <Button
            variant="ghost"
            size="lg"
            onClick={handleFlip}
            data-testid="button-flip"
          >
            <RotateCw className="h-5 w-5 mr-2" />
            카드 뒤집기
          </Button>

          <Button
            variant="outline"
            size="lg"
            onClick={handleNext}
            disabled={currentIndex === words.length - 1}
            data-testid="button-next"
          >
            다음
            <ChevronRight className="h-5 w-5 ml-2" />
          </Button>
        </div>

        {/* Tips */}
        <div className="mt-8 p-4 bg-muted/50 rounded-md border border-border">
          <h3 className="font-semibold mb-2">💡 플래시카드 사용법</h3>
          <ul className="text-sm text-muted-foreground space-y-1">
            <li>• 카드를 클릭하거나 "카드 뒤집기" 버튼을 눌러 뜻을 확인하세요</li>
            <li>• "모름"과 "아리송"으로 표시된 단어를 우선적으로 복습합니다</li>
            <li>• 카드 뒤집은 후 숙련도 버튼으로 학습 진도를 표시하세요</li>
            <li>• "알고있음"으로 표시하면 자동으로 다음 카드로 넘어갑니다</li>
            <li>• 정기적인 복습이 장기 기억에 도움이 됩니다</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
